import { type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  FormControl,
  type FormControlProps,
  FormHelperText,
  Typography,
} from "@mui/material";

import { TimePicker, TimePickerProps } from "@mui/x-date-pickers";
import { parse } from "date-fns";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type TextInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  size?: FormControlProps["size"];
  hideLabel?: boolean;
  hideError?: boolean;
  fullWidth?: boolean;
  hide?: boolean;
  control: Control<T>;
} & TimePickerProps;

export const TimeCronInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  size,
  fullWidth,
  control,
  hide,
  ...inputProps
}: TextInputProps<T>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <Box display={"flex"} flexDirection={"column"} gap={0.5} width={"100%"}>
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth={fullWidth} error={!!error} size={size}>
        <TimePicker
          {...inputProps}
          {...field}
          value={
            field.value
              ? parse(
                  `${field.value.hour}:${field.value.minute}:00`,
                  "HH:mm:ss",
                  new Date(),
                )
              : null
          }
          onChange={(value) => {
            const newValue = value
              ? {
                  hour: value.getHours().toString().padStart(2, "0"),
                  minute: value.getMinutes().toString().padStart(2, "0"),
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }
              : value;

            field.onChange(newValue);
          }}
        />
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
